import React, { useCallback, useEffect, useMemo } from 'react';
import { Box, Button, CircularProgress, Stack, useTheme } from '@mui/material';
import { Add } from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { PageHeader } from '@/components/layouts/PageHeader';
import { PageTabsBar } from '@/components/layouts/PageHeader/PageTabsBar';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import { openDialog } from '@/features/dialog/dialogSlice';
import RelationshipContactsTable from '@/components/tables/RelationshipContactsTable';
import RelationshipCompaniesTable from '@/components/tables/RelationshipCompaniesTable';
import InsightContactsTable from '@/components/tables/InsightContactsTable';
import { RelationshipsIcon } from '@/components/icons/RelationshipsIcon';

const COMPANIES_TAB = { label: 'Companies', value: 'companies' };
const CONTACTS_TAB = { label: 'Contacts', value: 'contacts' };
const INSIGHT_CONTACTS_TAB = { label: 'Insight Contacts', value: 'clientContacts' };

export const RelationshipsView = () => {
	const theme = useTheme();
	const dispatch = useDispatch();
	const [searchParams, setSearchParams] = useSearchParams();
	const { getFeatureFlags, isLoading: isFeatureFlagsLoading } = useFeatureFlagContext();
	const isRelationshipContactsEnabled = Boolean(getFeatureFlags('relationshipContacts')?.active);

	
	// relationshipContacts: Companies + Contacts, or Contacts + Insight Contacts
	const tabs = useMemo(
		() =>
			isRelationshipContactsEnabled
				? [COMPANIES_TAB, CONTACTS_TAB]
				: [CONTACTS_TAB, INSIGHT_CONTACTS_TAB],
		[isRelationshipContactsEnabled],
	);

	const defaultTab = isRelationshipContactsEnabled ? 'companies' : 'contacts';
	const currentTab = searchParams.get('tab') || defaultTab;

	const handleTabChange = useCallback(
		(tab) => {
			if (tab === 'companies' && !isRelationshipContactsEnabled) {
				return;
			}

			const next = new URLSearchParams(searchParams);
			next.set('tab', tab);
			setSearchParams(next);
		},
		[isRelationshipContactsEnabled, searchParams, setSearchParams],
	);

	useEffect(() => {
		if (isFeatureFlagsLoading) {
			return;
		}
		if (!tabs.some((tab) => tab.value === currentTab)) {
			handleTabChange(defaultTab);
		}
	}, [currentTab, defaultTab, handleTabChange, isFeatureFlagsLoading, tabs]);

	const createButton =
		currentTab === 'companies' && isRelationshipContactsEnabled ? (
			<Button
				variant='contained'
				startIcon={<Add />}
				onClick={() =>
					dispatch(
						openDialog({
							dialogName: 'relationshipCompany',
							data: {},
						}),
					)
				}
			>
				New company
			</Button>
		) : currentTab === 'contacts' ? (
			<Button
				variant='contained'
				startIcon={<Add />}
				onClick={() =>
					dispatch(
						openDialog({
							dialogName: 'relationshipContact',
							data: {},
						}),
					)
				}
			>
				New contact
			</Button>
		) : null;

	if (isFeatureFlagsLoading) {
		return (
			<Box pt={1} display='flex' justifyContent='center'>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Box pt={1}>
			<PageHeader
				title='Relationships'
				icon={
					<RelationshipsIcon
						{...(theme.palette.mode === 'dark' && { bgColor: 'transparent' })}
					/>
				}
			/>
			<Stack>
				<PageTabsBar
					tabs={tabs}
					value={currentTab}
					onChange={(event, value) => {
						event.preventDefault();
						handleTabChange(value);
					}}
					queryParam='tab'
					actions={createButton}
				/>
				<Box px={3} sx={{ pt: 1 }}>
					{currentTab === 'companies' && isRelationshipContactsEnabled ? (
						<RelationshipCompaniesTable />
					) : currentTab === 'clientContacts' && !isRelationshipContactsEnabled ? (
						<InsightContactsTable />
					) : (
						<RelationshipContactsTable status='active' />
					)}
				</Box>
			</Stack>
		</Box>
	);
};
